import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";

import { canDownload, handleDownload } from "../../api/api";

function getErrorMessage(err) {
    if (isAxiosError(err)) {
        if (err.response) {
            const status = err.response.status;
            const message = err.response.data || "Request failed";

            if (status === 400) {
                return `Invalid request: ${message}`;
            }

            if (status === 403) {
                return `Access denied: ${message}`;
            }

            if (status === 404) {
                return "Service not found";
            }

            if (status >= 500) {
                return "Server error. Try again later.";
            }

            return typeof message === "string" ? message : "Request failed";
        }

        if (err.request) {
            return "Network error. Check your connection.";
        }

        return err.message;
    }

    if (err instanceof Error) {
        return err.message;
    }

    return "Unknown error";
}

export function DownloadButton({
    as: Component = Button,
    downloadItems,
    checkAvailability = true,
    filename = "files.zip",
    buttonText = "Download",
    variant,
    size,
    className,
    icon,
}) {
    const [isChecking, setIsChecking] = useState(checkAvailability);
    const [isAvailable, setIsAvailable] = useState(false);
    const [isStartingDownload, setIsStartingDownload] = useState(false);

    const [error, setError] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);

    async function resolveDownloadItems() {
        if (typeof downloadItems === "function") {
            return await downloadItems();
        }
        return downloadItems;
    }

    useEffect(() => {
        let mounted = true;

        async function runCheck(items) {
            if (!items || items.length === 0) {
                setIsAvailable(false);
                setIsChecking(false);
                return;
            }

            setIsChecking(true);
            setIsAvailable(false);

            try {
                const ok = await canDownload(items);

                if (mounted) {
                    setIsAvailable(ok);
                }
            } catch (err) {
                console.error(err);

                if (mounted) {
                    setIsAvailable(false);
                }
            } finally {
                if (mounted) {
                    setIsChecking(false);
                }
            }
        }

        if (checkAvailability && typeof downloadItems !== "function") {
            runCheck(downloadItems);
        } else {
            setIsChecking(false);
            setIsAvailable(!checkAvailability); // if no check → allow
        }

        return () => {
            mounted = false;
        };
    }, [downloadItems, checkAvailability]);

    async function onClick() {
        if (isChecking || isStartingDownload) return;

        try {
            setIsStartingDownload(true);

            const items = await resolveDownloadItems();

            if (!items || items.length === 0) {
                throw new Error("No items found for download");
            }

            await handleDownload(items, filename);
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err));
            setShowErrorModal(true);
        } finally {
            setIsStartingDownload(false);
        }
    }

    const isAsync = typeof downloadItems === "function";
    const disabled =
        isChecking ||
        isStartingDownload ||
        (!isAsync && checkAvailability && !isAvailable);

    return (
        <>
            <Component
                onClick={onClick}
                disabled={disabled}
                variant={variant}
                size={size}
                className={className}
            >
                {icon}
                {isChecking
                    ? "Checking..."
                    : isStartingDownload
                      ? "Preparing..."
                      : buttonText}
            </Component>

            <Modal
                show={showErrorModal}
                onHide={() => setShowErrorModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Download failed</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error ||
                        "Something went wrong while preparing the download."}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowErrorModal(false)}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
